import React, { useMemo, memo, useCallback } from "react";
import { useMedia } from "../../contexts/MediaContext";

const MediaGallery = ({ onUpload }) => {
  const {
    mediaFiles,
    selectedMediaFile,
    selectedMediaFiles,
    selectionMode,
    selectMedia,
    closeMedia,
    confirmMediaSelection,
    handleDeleteMedia,
    handleDeleteMediaBatch,
    bytesToMB,
    currentMediaType,
  } = useMedia();

  // メディアタイプに基づいてフィルタリング
  const filteredMediaFiles = useMemo(() => {
    if (!currentMediaType) return mediaFiles;
    return mediaFiles.filter((mediaFile) => {
      if (currentMediaType === "image") {
        return mediaFile.mime_type && mediaFile.mime_type.startsWith("image/");
      } else if (currentMediaType === "video") {
        return mediaFile.mime_type && mediaFile.mime_type.startsWith("video/");
      }
      return true;
    });
  }, [mediaFiles, currentMediaType]);

  // 複数選択モードでも最後に選択したメディアを表示するための処理
  const displayMedia = useMemo(() => {
    return selectionMode === "multiple" && selectedMediaFiles.length > 0
      ? selectedMediaFiles[selectedMediaFiles.length - 1]
      : selectedMediaFile;
  }, [selectionMode, selectedMediaFile, selectedMediaFiles]);

  // ファイルタイプの検証関数
  const validateFileType = (file, mediaType) => {
    if (!file || !mediaType) return true;
    if (mediaType === "image") {
      return file.type.startsWith("image/");
    } else if (mediaType === "video") {
      return file.type.startsWith("video/");
    }

    return true;
  };

  // ファイルアップロードハンドラー
  const handleFileUpload = useCallback(
    (e) => {
      if (e.preventDefault) {
        e.preventDefault();
      }

      const files = e.target?.files || (e.dataTransfer && e.dataTransfer.files);
      if (!files || files.length === 0) return;

      // ファイルタイプの検証
      const file = files[0]; // 最初のファイルを取得
      const isValidType = validateFileType(file, currentMediaType);

      if (!isValidType) {
        alert(
          currentMediaType === "image"
            ? "画像ファイルのみアップロードできます。"
            : "動画ファイルのみアップロードできます。"
        );
        return;
      }

      // 有効なファイルの場合、アップロード処理を続行
      onUpload(e);
    },
    [currentMediaType, onUpload]
  );

  return (
    <div className="mediainner">
      {/* メディアを閉じる */}
      <i
        className="mediaCloseBtn fa-duotone fa-thin fa-xmark-large"
        onClick={closeMedia}
      ></i>
      {/* アップロード */}
      <section
        className="img-form"
        onDrop={handleFileUpload}
        onDragOver={(e) => {
          e.preventDefault();
        }}
      >
        <button
          type="button"
          className="file img"
          onClick={() => {
            const fileInput = document.querySelector(
              '.mediainner input[type="file"]'
            );
            if (fileInput) fileInput.click();
          }}
        >
          <input
            type="file"
            style={{ display: "none" }}
            onChange={handleFileUpload}
            accept={
              currentMediaType === "image"
                ? "image/*"
                : currentMediaType === "video"
                ? "video/*"
                : undefined
            }
          />
          <span className="memo">
            {currentMediaType === "image"
              ? "Image"
              : currentMediaType === "video"
              ? "Video"
              : "Media"}
            <br />
            Drag & Drop / File
          </span>
        </button>
      </section>

      {/* 画像動画リスト */}
      <section className="medias">
        <div className="media-inner">
          {filteredMediaFiles.map((mediaFile) => (
            <MediaItem
              key={mediaFile.id}
              mediaFile={mediaFile}
              isSelected={
                selectionMode === "multiple"
                  ? selectedMediaFiles.some((item) => item.id === mediaFile.id)
                  : selectedMediaFile && selectedMediaFile.id === mediaFile.id
              }
              onClick={selectMedia}
            />
          ))}
        </div>
      </section>

      {/* プレビュー */}
      <section className="preview">
        {/* 複数選択モードでも単一選択モードと同じプレビュー表示 */}
        <div className="prBox">
          {(() => {
            if (displayMedia && displayMedia.image_url) {
              const isImage =
                displayMedia.mime_type &&
                displayMedia.mime_type.startsWith("image");
              const isVideo =
                displayMedia.mime_type &&
                displayMedia.mime_type.startsWith("video");

              if (isImage) {
                return (
                  <img
                    src={displayMedia.image_url}
                    alt={`Media ${displayMedia.id}`}
                    loading="lazy"
                  />
                );
              }
              if (isVideo) {
                return (
                  <video
                    src={displayMedia.image_url}
                    alt={`Media ${displayMedia.id}`}
                    autoPlay
                    loop
                    muted
                    loading="lazy"
                  />
                );
              }
            }
            return null;
          })()}
        </div>

        <p>
          {selectionMode === "multiple" && selectedMediaFiles.length > 0 ? (
            <>
              {displayMedia && displayMedia.image_url
                ? `File: ${displayMedia.image_url.substring(
                    displayMedia.image_url.indexOf(".com/") + 5
                  )}`
                : ""}
              <br />
              {displayMedia && displayMedia.width && displayMedia.height
                ? `Size: ${displayMedia.width}px*${displayMedia.height}px`
                : ""}
              {displayMedia &&
                displayMedia.width &&
                displayMedia.height &&
                displayMedia.file_size &&
                "/"}
              {displayMedia && displayMedia.file_size
                ? `${bytesToMB(displayMedia.file_size)} MB`
                : ""}
              <br />
              <span className="selection-count">
                {selectedMediaFiles.length}件選択中
              </span>
            </>
          ) : (
            displayMedia && (
              <>
                {displayMedia.image_url
                  ? `File: ${displayMedia.image_url.substring(
                      displayMedia.image_url.indexOf(".com/") + 5
                    )}`
                  : ""}
                <br />
                {displayMedia.width && displayMedia.height
                  ? `Size: ${displayMedia.width}px*${displayMedia.height}px`
                  : ""}
                {displayMedia.width &&
                  displayMedia.height &&
                  displayMedia.file_size &&
                  "/"}
                {displayMedia.file_size
                  ? `${bytesToMB(displayMedia.file_size)} MB`
                  : ""}
              </>
            )
          )}
        </p>
      </section>

      {/* サブミット */}
      <section className="buttonArea">
        <button
          onClick={confirmMediaSelection}
          disabled={
            selectionMode === "multiple"
              ? selectedMediaFiles.length === 0
              : !selectedMediaFile
          }
        >
          Submit
        </button>
        <button
          onClick={() => {
            if (selectionMode === "multiple") {
              if (
                selectedMediaFiles.length > 0 &&
                window.confirm(
                  `選択中の${selectedMediaFiles.length}件のメディアを削除してもよろしいですか？`
                )
              ) {
                // 選択中の全メディアをバッチ削除
                handleDeleteMediaBatch(selectedMediaFiles);
              }
            } else {
              if (
                selectedMediaFile &&
                window.confirm("このメディアを削除してもよろしいですか？")
              ) {
                handleDeleteMedia(selectedMediaFile);
              }
            }
          }}
          disabled={
            selectionMode === "multiple"
              ? selectedMediaFiles.length === 0
              : !selectedMediaFile
          }
        >
          Delete
        </button>
      </section>
    </div>
  );
};

// メディアアイテムの最適化されたコンポーネント
const MediaItem = memo(({ mediaFile, isSelected, onClick }) => {
  const isImage =
    mediaFile.mime_type && mediaFile.mime_type.startsWith("image");
  const isVideo =
    mediaFile.mime_type && mediaFile.mime_type.startsWith("video");

  const handleClick = useCallback(() => {
    onClick(mediaFile);
  }, [onClick, mediaFile]);

  return (
    <figure
      className={`media-item ${isSelected ? "active" : ""}`}
      onClick={handleClick}
    >
      {isImage && <i className="fa-regular fa-image"></i>}
      {isVideo && <i className="fa-regular fa-video"></i>}

      {isImage ? (
        <img
          src={mediaFile.thumbnail_url || mediaFile.image_url}
          alt={`Media ${mediaFile.id}`}
          loading="lazy"
        />
      ) : isVideo ? (
        <img
          src={mediaFile.thumbnail_url || mediaFile.image_url}
          alt={`Video ${mediaFile.id}`}
          loading="lazy"
        />
      ) : (
        <img
          src={mediaFile.thumbnail_url || mediaFile.image_url}
          alt={`Media ${mediaFile.id}`}
          loading="lazy"
        />
      )}
    </figure>
  );
});

MediaItem.displayName = "MediaItem";

export default memo(MediaGallery);
